import { type SocketStatus } from "@client/ImperativeState";
import { useHeaderConnection } from "@client/HeaderConnectionContext";
import type { RawSocketFrame } from "@client/ImperativeState";
import { isIPadCapacitor } from "@client/lib/isIPad";
import { ShowEventLogItem } from "@client/pages/round/logs/ShowEventLogItem";
import type {
  IncomingActionMessage,
  IncomingUnconfirmedActionMessage,
} from "@engine/core";
import {
  buildSyncCanaryDiagnostics,
  buildSyncCanaryIncidentKey,
} from "@client/components/utils/syncCanaryDiagnostics";
import { isTransientIndexedDbDisconnectError } from "@engine/storageAdapters/indexedDbErrors";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import * as Sentry from "@client/telemetry/sentry";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sfc/ui";
import {
  CloudAlertIcon,
  CloudCheckIcon,
  CloudOffIcon,
  CloudUploadIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { Socket as IoSocket } from "socket.io-client";

function explanationOfDisconnectReason(
  reason: IoSocket.DisconnectReason,
  details?: {
    description?: unknown;
    roundId?: string;
    clientId?: string;
    recentRawFrames?: RawSocketFrame[];
  },
) {
  switch (reason) {
    case "io client disconnect":
      return "The socket was manually disconnected by the client. This should never happen -- please report it to S-Process admins.";
    case "io server disconnect":
      return "The server forcefully disconnected the socket. This should never happen -- please report it to S-Process admins.";
    case "ping timeout":
      return "The server did not respond to a ping. Maybe it's down?";
    case "transport close":
      return "The connection was closed. Did you lose your internet connection or change networks?";
    case "transport error":
      return "The connection encountered an error.";
    case "parse error": {
      const { description, roundId, clientId, recentRawFrames } = details ?? {};
      Sentry.withScope((scope) => {
        scope.setLevel("error");
        scope.setTag("sync_issue", "socket_parse_error_disconnect");
        scope.setTag("socket_disconnect_reason", reason);
        // Correlate with the server-side action-sync warning (Docker logs) and
        // the confirmed_action_apply_failed reports, which carry the same tags.
        if (roundId) scope.setTag("round_id", roundId);
        if (clientId) scope.setTag("client_id", clientId);
        // socket.io-parser throws a bare Error without the offending bytes, so
        // surface the raw frames we buffered off the transport. The last frame
        // is almost always the one that failed to decode.
        if (recentRawFrames?.length) {
          scope.setContext("socket_recent_raw_frames", {
            count: recentRawFrames.length,
            frames: recentRawFrames,
          });
        }
        if (description instanceof Error) {
          scope.setExtra("socketDisconnectErrorName", description.name);
          scope.setExtra("socketDisconnectErrorMessage", description.message);
          Sentry.captureException(description);
        } else {
          scope.setContext("socket_disconnect_description", { description });
          Sentry.captureException(
            new Error("Socket.IO parse error disconnect"),
          );
        }
      });
      return "The connection received an invalid message. This should never happen -- please report it to S-Process admins.";
    }
    default:
      assertUnreachable(reason);
  }
}

/**
 * The status icon and human explanation for the current connection state.
 * Exported so tests exercise the component's own logic — the rendered
 * explanation lives inside a closed tooltip.
 */
export function connectionStatusPresentation({
  sessionRejected,
  schemaSkew,
  socketStatus,
  unconfirmedActionCount,
  actionsAreTooOld,
}: {
  sessionRejected: boolean;
  schemaSkew: boolean;
  socketStatus: SocketStatus;
  unconfirmedActionCount: number;
  actionsAreTooOld: boolean;
}): { explanation: React.ReactNode; socketStatusIcon: React.ReactNode } {
  let explanation: React.ReactNode = "";
  let socketStatusIcon: React.ReactNode;
  if (sessionRejected) {
    // The server refused this session's socket (expired/invalid token) —
    // permanent for this session, and retrying with the same credentials
    // cannot succeed. The cold-boot path lands a dead session on the sign-in
    // screen with the offline queue intact, so reopening is the way out.
    socketStatusIcon = <CloudOffIcon className="size-6 text-destructive" />;
    explanation = (
      <div className="flex flex-col gap-2">
        <div>
          Your session has expired, so changes can no longer sync.{" "}
          {isIPadCapacitor()
            ? "Close and reopen the app to sign in again — changes saved on this iPad are kept."
            : "Refresh the page and sign in again."}
        </div>
        <div>We will not reconnect</div>
      </div>
    );
  } else if (schemaSkew) {
    // Deliberate permanent disconnect: this build failed to parse a server
    // message, so it's older than the deployed action schema. Explain the
    // way out instead of the generic "manual disconnect" warning below.
    socketStatusIcon = <CloudOffIcon className="size-6 text-destructive" />;
    explanation = (
      <div className="flex flex-col gap-2">
        <div>
          This version of the app is older than the server and can no longer
          sync.{" "}
          {isIPadCapacitor()
            ? "Update the app from TestFlight, then reopen it."
            : "Refresh the page to get the latest version."}
        </div>
        <div>We will not reconnect</div>
      </div>
    );
  } else if (socketStatus === "connected") {
    explanation = "connected";
    if (unconfirmedActionCount === 0) {
      socketStatusIcon = (
        <CloudCheckIcon className="size-6 text-text-secondary" />
      );
    } else if (actionsAreTooOld) {
      socketStatusIcon = <CloudAlertIcon className="size-6 text-destructive" />;
    } else {
      socketStatusIcon = (
        <CloudUploadIcon className="size-6 text-text-secondary" />
      );
    }
  } else if (socketStatus === null) {
    socketStatusIcon = <CloudOffIcon className="size-6 text-yellow-400" />;
    explanation = "unknown";
  } else {
    socketStatusIcon = <CloudOffIcon className="size-6 text-text-secondary" />;
    let description = explanationOfDisconnectReason(socketStatus.reason, {
      description: socketStatus.description,
      roundId: socketStatus.roundId,
      clientId: socketStatus.clientId,
      recentRawFrames: socketStatus.recentRawFrames,
    });
    if (
      socketStatus.description &&
      !(socketStatus.description instanceof Error)
    ) {
      description = socketStatus.description.description;
    }
    explanation = (
      <div className="flex flex-col gap-2">
        <div>{description}</div>
        <div>
          {socketStatus.willReconnect
            ? "We will reconnect when back online"
            : "We will not reconnect"}
        </div>
      </div>
    );
  }

  return { explanation, socketStatusIcon };
}

export function ConnectionStatusDisplay() {
  const connectionData = useHeaderConnection();
  const [unconfirmedActions, setUnconfirmedActions] = useState<
    { createdAt: number; message: IncomingUnconfirmedActionMessage }[]
  >([]);
  const [actionsAreTooOld, setActionsAreTooOld] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reportedOldestCreatedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!connectionData) return;
    let isActive = true;
    void connectionData.unconfirmedActions
      .then((acts) => {
        if (!isActive) return;

        setUnconfirmedActions(acts);
        setActionsAreTooOld(false);

        if (timerRef.current != null) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }

        if (acts.length === 0) {
          reportedOldestCreatedAtRef.current = null;
          return;
        }

        const oldestCreatedAt = acts[0].createdAt;
        const oldestAge = Date.now() - oldestCreatedAt;
        const reportIfNeeded = () => {
          if (!isActive) return;
          setActionsAreTooOld(true);
          if (reportedOldestCreatedAtRef.current === oldestCreatedAt) return;
          reportedOldestCreatedAtRef.current = oldestCreatedAt;
          // One stuck edit fires this in every open tab of the round (shared
          // queue); incident_key lets Sentry group those into one incident.
          Sentry.withScope((scope) => {
            scope.setLevel("warning");
            scope.setTag("sync_issue", "unconfirmed_actions_stale");
            scope.setTag("client_id", connectionData.clientId);
            scope.setTag(
              "incident_key",
              buildSyncCanaryIncidentKey(
                connectionData.round.id,
                oldestCreatedAt,
              ),
            );
            scope.setContext(
              "sync_canary",
              buildSyncCanaryDiagnostics({
                unconfirmedActions: acts,
                socketStatus: connectionData.socketStatus,
                now: Date.now(),
              }),
            );
            Sentry.captureMessage(
              "Unconfirmed actions are older than 5 seconds",
            );
          });
        };

        if (oldestAge > 5_000) {
          reportIfNeeded();
          return;
        }

        reportedOldestCreatedAtRef.current = null;
        timerRef.current = setTimeout(reportIfNeeded, 5_000 - oldestAge);
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        if (isTransientIndexedDbDisconnectError(error)) {
          console.warn(
            "[action-sync][connection-status] transient IndexedDB disconnect while reading unconfirmed actions",
            { error },
          );
          return;
        }
        Sentry.captureException(error);
      });

    return () => {
      isActive = false;
      if (timerRef.current != null) clearTimeout(timerRef.current);
    };
  }, [connectionData]);

  if (!connectionData) return null;

  const {
    socketStatus,
    schemaSkew,
    sessionRejected,
    round,
    unconfirmedActions: _unconfirmedActionsPromise,
    retryUnconfirmedActions,
    clearUnconfirmedActions,
    reportError,
  } = connectionData;

  const { explanation, socketStatusIcon } = connectionStatusPresentation({
    sessionRejected,
    schemaSkew,
    socketStatus,
    unconfirmedActionCount: unconfirmedActions.length,
    actionsAreTooOld,
  });

  return (
    <Dialog>
      <Tooltip>
        <DialogTrigger
          disabled={unconfirmedActions.length === 0}
          render={
            <TooltipTrigger
              render={
                <button
                  className="mr-1 text-2xl cursor-pointer disabled:cursor-default"
                  disabled={unconfirmedActions.length === 0}
                >
                  {socketStatusIcon}
                </button>
              }
            />
          }
        />
        <TooltipContent onPointerDown={(e) => e.stopPropagation()}>
          <div>
            <span className="font-bold">Connection Status:</span> {explanation}
          </div>
          {unconfirmedActions.length === 0 && (
            <div>All actions are synced with the server</div>
          )}
          {unconfirmedActions.length > 0 && (
            <DialogTrigger
              render={<button className="text-destructive cursor-pointer" />}
            >
              {unconfirmedActions.length === 1
                ? "See one pending action"
                : `See ${unconfirmedActions.length} pending actions`}
            </DialogTrigger>
          )}
        </TooltipContent>
      </Tooltip>
      <DialogContent className="flex flex-col gap-2 min-w-96 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pending Actions</DialogTitle>
        </DialogHeader>
        <div className="flex flex-row gap-2">
          <Button onClick={() => void retryUnconfirmedActions()}>
            Retry Actions
          </Button>
          <DialogClose
            render={
              <Button
                onClick={() => {
                  void clearUnconfirmedActions();
                }}
              />
            }
          >
            Clear Actions
          </DialogClose>
          <Button
            onClick={async () => {
              await reportError();
              toast.success("Error reported");
            }}
          >
            Report Error
          </Button>
        </div>
        <div className="border">
          {unconfirmedActions.map(({ message }, i) => {
            const actionMessage: IncomingActionMessage = {
              ...message,
              ignoredReason: null,
              version: i,
              timestamp: new Date().toISOString(),
            };
            return (
              <ShowEventLogItem
                key={message.id}
                item={actionMessage}
                round={round}
              />
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
